import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Alert from '../components/Alert';
import Spinner from '../components/Spinner';
import BCPPageTitle from '../components/BCPPageTitle';
import GuideTopAI from '../components/GuideTopAI';
import RedButton from '../components/RedButton';
import Button from '../parts/button';
import BasicBCPStepFlow from '../components/BasicBCPStepFlow';
import BCPDownloadModal from '../components/BCPDownloadModal';
import { createApiRequest } from '../utils/apiResponse';
import { navigationService } from '../utils/navigation';
import { bcpService } from '../services/bcpService';
import { apiClient } from '../services/api';
import {
  BCPFormContainer,
  BCPFormLayout,
  BCPSectionCard,
  ReferenceMaterialsPanel,
} from '../components/BCP';
import { INITIAL_RESPONSE_SECTIONS } from '../config/initialResponseSections';
import { useBCPDynamicForm } from '../hooks/useBCPDynamicForm';
import { useAIConsultContext } from '../hooks/useAIConsultContext';
import { useBCPLoader } from '../hooks/useBCPLoader';
import { useAutoDismissMessage } from '../hooks/useAutoDismissMessage';
import { useBeforeUnload } from '../hooks/useBeforeUnload';
import { saveBasicBcpSection, saveAllDraftSections } from '../utils/basicBcpStorage';

interface NavigationState {
  uploadedFiles: any[];
  apiResponse?: any;
  regulationData?: Record<string, string>;
  regulationFormData?: Record<string, string>;
  restorePolicyData?: Record<string, string>;
  restorePolicyFormData?: Record<string, string>;
  initialResponseData?: Record<string, string>;
}

const INITIAL_RESPONSE_FIELD_IDS = INITIAL_RESPONSE_SECTIONS.flatMap((section) =>
  section.fields
    .filter((field) => field.type !== 'static')
    .map((field) => field.id)
);

const extractInitialResponseValues = (
  source?: Record<string, any>
): Record<string, string> => {
  if (!source) {
    return {};
  }

  const result: Record<string, string> = {};

  INITIAL_RESPONSE_FIELD_IDS.forEach((fieldId) => {
    const value = source[fieldId];
    // undefinedとnullのみを除外し、空文字列も保持する
    if (value !== undefined && value !== null && typeof value === 'string') {
      result[fieldId] = value;
    }
  });

  return result;
};

const InitialResponsePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ bcpId?: string }>();
  const [searchParams] = useSearchParams();

  const navigationState = navigationService.extractLocationState<NavigationState>(
    location,
    {
      uploadedFiles: [],
      apiResponse: undefined,
      regulationData: undefined,
      regulationFormData: undefined,
      restorePolicyData: undefined,
      restorePolicyFormData: undefined,
      initialResponseData: undefined,
    }
  );

  const { uploadedFiles } = navigationState;

  const [submitLoading, setSubmitLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(3);
  const { message: successMessage, showMessage: setSuccessMessage } = useAutoDismissMessage();
  const hasLoadedInitialData = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  // ページロード時に上部にスクロール
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 「次へ」「戻る」ボタンでのナビゲーション中はブロックしない
  const isNavigatingRef = useRef(false);

  const bcpId = params.bcpId ?? 'new';
  const tenantIdFromQuery = searchParams.get('tenantId');

  // BCP ID and tenant ID resolved values
  const resolvedBcpId = bcpId;
  const resolvedTenantId = tenantIdFromQuery;

  // Load BCP document
  const {
    loading: bcpLoading,
    isEditMode,
    bcpDocument,
    getInitialFormData,
  } = useBCPLoader({
    bcpId: bcpId === 'new' ? null : bcpId,
    tenantId: tenantIdFromQuery,
    onError: (error) => {
      setGeneralErrors([error]);
    },
  });

  // ダウンロード時に自動保存されたBCPを保持
  const [autoSavedBcp, setAutoSavedBcp] = useState<any>(null);

  // Extract apiResponse from bcpDocument if in edit mode
  const effectiveApiResponse = useMemo(() => {
    console.log('[InitialResponsePage] effectiveApiResponse calculation started');
    console.log('[InitialResponsePage] navigationState.apiResponse:', navigationState.apiResponse);
    console.log('[InitialResponsePage] bcpDocument:', bcpDocument);

    if (navigationState.apiResponse) {
      console.log('[InitialResponsePage] Using navigationState.apiResponse');
      return navigationState.apiResponse;
    }
    // If in edit mode, try to extract from bcpDocument
    if (bcpDocument) {
      console.log('[InitialResponsePage] bcpDocument exists, extracting company_info');
      // Try to get company_info from content.sections.restore_policy first
      let companyInfo = bcpDocument.content?.sections?.restore_policy?.company_info;
      console.log('[InitialResponsePage] company_info from content.sections.restore_policy:', companyInfo);

      // Fallback to form_data for compatibility
      if (!companyInfo && bcpDocument.form_data) {
        companyInfo = bcpDocument.form_data.company_info;
        console.log('[InitialResponsePage] company_info from form_data:', companyInfo);
      }

      // Extract address from company_info
      let address = '';
      if (companyInfo) {
        const match = companyInfo.match(/所在地[:\s：]*([^\n]+)/);
        console.log('[InitialResponsePage] regex match result:', match);
        if (match) {
          address = match[1].trim();
          console.log('[InitialResponsePage] extracted address:', address);
        }
      }

      if (address) {
        const result = {
          extracted_info: { address },
          merged_extracted_info: { address },
        };
        console.log('[InitialResponsePage] Returning result:', result);
        return result;
      }
    }
    console.log('[InitialResponsePage] No address found, returning undefined');
    return navigationState.apiResponse;
  }, [navigationState.apiResponse, bcpDocument]);

  const initialFormValues = extractInitialResponseValues(navigationState.initialResponseData);

  const form = useBCPDynamicForm({
    sections: INITIAL_RESPONSE_SECTIONS,
    initialValues: initialFormValues,
  });

  const { values, setFieldValue, errors: fieldErrors, validate } = form;

  // 初期値を保持（変更検知の基準値として使用）
  const initialValuesRef = useRef<Record<string, string> | undefined>(undefined);

  // isDirtyを計算（初期値と現在の値を比較）
  const isDirty = useMemo(() => {
    if (!initialValuesRef.current) return false;
    return Object.keys(values).some(
      key => values[key] !== (initialValuesRef.current?.[key] || '')
    );
  }, [values]);

  // 離脱確認ダイアログ（フォームが変更されていない、または処理中はスキップ）
  useBeforeUnload({
    enabled: true,
    shouldBypass: () => {
      // 「次へ」「戻る」ボタンでのナビゲーション中はブロックしない
      if (isNavigatingRef.current) return true;
      const shouldSkip = !isDirty || isSaving || downloadLoading;
      console.log('[InitialResponsePage] shouldBypass check:', { isDirty, isSaving, downloadLoading, shouldSkip });
      return shouldSkip;
    },
  });

  // BCP ID/Tenant IDの変更を検知してリセット
  const bcpIdRef = useRef(resolvedBcpId);
  const tenantIdRef = useRef(resolvedTenantId);

  useEffect(() => {
    const bcpChanged = resolvedBcpId !== bcpIdRef.current;
    const tenantChanged = resolvedTenantId !== tenantIdRef.current;

    if (bcpChanged || tenantChanged) {
      console.log('[InitialResponsePage] BCP or tenant changed, resetting state');
      bcpIdRef.current = resolvedBcpId;
      tenantIdRef.current = resolvedTenantId;
      hasLoadedInitialData.current = false;
      initialValuesRef.current = undefined;
    }
  }, [resolvedBcpId, resolvedTenantId]);

  const aiConsultContext = useAIConsultContext({
    sections: INITIAL_RESPONSE_SECTIONS,
    values,
    bcpType: 'basic',
    onFieldValueChange: setFieldValue,
  });

  useEffect(() => {
    if (navigationState.initialResponseData) {
      setSuccessMessage('初動対応データを読み込みました。');
    }
  }, [navigationState.initialResponseData]);

  // Auto-save form data to localStorage (debounced)
  useEffect(() => {
    // Skip if loading or no data loaded yet
    if (!hasLoadedInitialData.current || Object.keys(values).length === 0) {
      return;
    }

    // Skip if no bcpId
    if (resolvedBcpId === 'new') {
      return;
    }

    // Save immediately to ensure latest data is available for PDF download
    // No debounce to prevent timing issues with PDF download
    saveBasicBcpSection(resolvedBcpId, resolvedTenantId, 'initial_response', values);
  }, [values, resolvedBcpId, resolvedTenantId]);

  // Check if we have saved initial response data
  const hasSavedData = useMemo(() => {
    if (navigationState.initialResponseData && Object.keys(navigationState.initialResponseData).length > 0) {
      return true;
    }
    // Check if initial_response section exists in bcpDocument
    // This is more reliable than checking form_data, as form_data may contain
    // fields from other sections
    if (bcpDocument?.content?.sections?.initial_response) {
      return true;
    }
    return false;
  }, [navigationState.initialResponseData, bcpDocument]);

  // Load saved data when in edit mode
  useEffect(() => {
    if (isEditMode && bcpDocument && !hasLoadedInitialData.current) {
      console.log('[InitialResponsePage] Loading initial data in edit mode');
      const rawData = getInitialFormData();
      const initialData = extractInitialResponseValues(rawData as Record<string, string>);

      if (Object.keys(initialData).length > 0) {
        console.log('[InitialResponsePage] Setting initial values:', Object.keys(initialData));
        Object.entries(initialData).forEach(([key, value]) => {
          setFieldValue(key, value);
        });
      }
      // Mark as loaded regardless of whether data exists
      // This allows generation to proceed if floodStageGuidelines is empty
      hasLoadedInitialData.current = true;
      console.log('[InitialResponsePage] Initial data load complete');

      // 初期値を更新（変更検知の基準値として使用）
      initialValuesRef.current = initialData;
      console.log('[InitialResponsePage] Updated initialValuesRef after data load');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, bcpDocument]);

  const handleFieldChange = (fieldId: string, value: string) => {
    console.log('[InitialResponsePage] handleFieldChange called:', { fieldId });
    setFieldValue(fieldId, value);
    if (generalErrors.length > 0) {
      setGeneralErrors([]);
    }
  };

  const handleSave = async () => {
    // 保存中の場合は早期リターン
    if (isSaving) {
      return;
    }

    const isValid = validate();
    if (!isValid) {
      setGeneralErrors(['入力内容に不備があります。各項目を確認してください。']);
      return;
    }

    setIsSaving(true);
    setSubmitLoading(true);
    setSuccessMessage(null);

    try {
      const result = await bcpService.saveBasicSection(
        bcpId === 'new' ? null : bcpId,
        'initial_response',
        values,
        { current_step: 'initial_response' },
        undefined,
        'published'
      );

      // Update bcpId if this was a new document
      if (bcpId === 'new' && result.id) {
        navigate(`/bcp/${result.id}/initial-response`, {
          state: {
            ...location.state,
            bcpId: result.id,
          },
          replace: true,
        });
      }

      // 保存成功後、現在の値を新しい初期値として設定
      initialValuesRef.current = { ...values };
      console.log('[InitialResponsePage] Updated initialValuesRef after save');

      setSuccessMessage('初動対応データを保存しました。');
    } catch (error) {
      console.error('Error saving initial response:', error);
      setGeneralErrors(['初動対応データの保存に失敗しました。']);
    } finally {
      setSubmitLoading(false);
      setIsSaving(false);
    }
  };

  const handleDownload = async () => {
    // 保存中の場合は早期リターン
    if (isSaving) {
      return;
    }

    setCurrentStep(3);

    // ダウンロード前に現在のフォーム内容と全セクションを保存
    setIsSaving(true);
    try {
      console.log('[InitialResponsePage] Saving all sections before download...');
      const currentBcpId = bcpDocument?.id || resolvedBcpId;

      // Save current section and all draft sections from localStorage
      const finalBcpId = await saveAllDraftSections(
        currentBcpId,
        resolvedTenantId,
        bcpService,
        'initial_response',
        values
      );

      console.log('[InitialResponsePage] All sections saved successfully:', finalBcpId);
      setAutoSavedBcp({ id: finalBcpId });
      initialValuesRef.current = { ...values };
    } catch (error) {
      console.error('[InitialResponsePage] Failed to save all sections:', error);
      setGeneralErrors(['ダウンロード前の保存に失敗しました。']);
      setIsSaving(false);
      return;
    } finally {
      setIsSaving(false);
    }

    setIsDownloadModalOpen(true);
  };

  const handleSubmit = async () => {
    // 保存中の場合は早期リターン
    if (isSaving) {
      return;
    }

    const isValid = validate();
    if (!isValid) {
      setGeneralErrors(['入力内容に不備があります。各項目を確認してください。']);
      return;
    }

    // 「次へ」ボタンでのナビゲーション開始（ブロック解除）
    isNavigatingRef.current = true;

    // 自動保存されたBCPまたは既存のBCPがある場合、それを使用
    const nextBcpId = autoSavedBcp?.id || resolvedBcpId;

    // Build URL with tenantId if available
    let targetUrl = `/bcp/${nextBcpId}/procedure-manual`;
    if (resolvedTenantId) {
      targetUrl += `?tenantId=${resolvedTenantId}`;
    }

    console.log('[InitialResponsePage] Navigating to next page without saving');
    navigate(targetUrl, {
      state: {
        ...location.state,
        regulationData: navigationState.regulationData,
        restorePolicyData: navigationState.restorePolicyData,
        initialResponseData: values,
        uploadedFiles,
        apiResponse: effectiveApiResponse,
        fromPage: 'initial-response',
      },
    });
  };

  const handleBack = () => {
    // 「戻る」ボタンでのナビゲーション開始（ブロック解除）
    isNavigatingRef.current = true;

    // Build URL with tenantId if available
    let targetUrl = `/bcp/${bcpId}/regulation`;
    if (resolvedTenantId) {
      targetUrl += `?tenantId=${resolvedTenantId}`;
    }

    navigate(targetUrl, { state: location.state });
  };

  const handleStepNavigate = async (targetStep: string) => {
    // Mark as internal navigation to bypass beforeunload
    isNavigatingRef.current = true;

    // Save current form data to localStorage before navigation
    if (bcpId !== 'new') {
      console.log('[InitialResponsePage] Saving form data to localStorage before step navigation');
      saveBasicBcpSection(bcpId, resolvedTenantId, 'initial_response', values);
    }

    // Build target URL with tenantId
    let targetUrl = `/bcp/${bcpId}/${targetStep}`;
    if (resolvedTenantId) {
      targetUrl += `?tenantId=${resolvedTenantId}`;
    }

    console.log('[InitialResponsePage] Navigating to step:', targetStep, 'with state');

    // Navigate with complete state
    navigate(targetUrl, {
      state: {
        ...location.state,
        initialResponseData: values,
        fromPage: 'initial-response',
      },
    });
  };

  const aggregatedFieldErrors = useMemo(
    () => Object.values(fieldErrors),
    [fieldErrors]
  );

  return (
    <>
    <BCPFormLayout
      currentStep={2}
      showHeaderActions={true}
      onSave={handleSave}
      onDownload={handleDownload}
      saveLoading={submitLoading}
      downloadLoading={downloadLoading}
      saveDisabled={false}
      downloadDisabled={false}
      fieldContext={aiConsultContext.fieldContext}
      onAIContentApply={aiConsultContext.handleAIContentApply}
      bcpType="basic"
      bcpId={resolvedBcpId}
      topContent={
        <BasicBCPStepFlow
          currentStep="initial-response"
          bcpId={bcpId}
          tenantId={tenantIdFromQuery}
          onStepNavigate={handleStepNavigate}
        />
      }
    >
      <BCPFormContainer
        header={
          <>
            <BCPPageTitle title="基本版BCPシート（初動対応表）" />
            <div className="mt-6 flex justify-center">
              <GuideTopAI />
            </div>
          </>
        }
        alerts={
          <div className="space-y-4">
            {successMessage && <Alert type="success" message={successMessage} />}

            {generalErrors.length > 0 && (
              <Alert
                type="error"
                title="エラーが発生しました"
                messages={generalErrors}
              />
            )}

            {aggregatedFieldErrors.length > 0 && (
              <Alert
                type="error"
                title="入力内容を確認してください"
                messages={aggregatedFieldErrors}
              />
            )}
          </div>
        }
        footer={
          <>
            <ReferenceMaterialsPanel uploadedFiles={uploadedFiles} className="mt-6" />
            <div className="mt-8 flex justify-center gap-5">
              <RedButton onClick={handleBack}>
                戻る
              </RedButton>
              <RedButton onClick={handleSubmit} loading={submitLoading}>
                次へ
              </RedButton>
            </div>
          </>
        }
      >
        {INITIAL_RESPONSE_SECTIONS.map((section) => (
          <BCPSectionCard
            key={section.id}
            section={section}
            values={values}
            errors={fieldErrors}
            onChange={handleFieldChange}
            onAIConsult={aiConsultContext.handleAIConsult}
          />
        ))}
      </BCPFormContainer>
    </BCPFormLayout>

    {/* Download Modal */}
    <BCPDownloadModal
      isOpen={isDownloadModalOpen}
      onClose={() => {
        setIsDownloadModalOpen(false);
        setCurrentStep(3);
      }}
      bcpType="basic"
      bcpId={autoSavedBcp?.id || bcpDocument?.id || (bcpId !== 'new' ? bcpId : null)}
    />
  </>
  );
};

export default InitialResponsePage;
